import component from './ContactForm.vue';
import { StepId } from '~/consts/assistant-steps';
import {
  FormCheckboxValueType,
  FormMultipleChoiceValueType,
  FormTextFieldValueType,
  StepDefinition,
} from '@/interfaces/step-definition';
import { AssistantStepContext } from '@/interfaces/context';
import {
  buildFormCheckbox,
  buildFormMultipleChoiceField,
  buildFormTextField,
} from '@/lib/forms/builder';
import { ValidationType } from '@/consts/validation-types';
import { StepTypes } from '@/consts/step-types';
import i18n from '@/plugins/i18n';

export interface ContactFormStepContext extends AssistantStepContext {
  companyName: FormTextFieldValueType;
  firstname: FormTextFieldValueType;
  lastname: FormTextFieldValueType;
  street: FormTextFieldValueType;
  streetNumber: FormTextFieldValueType;
  zipcode: FormTextFieldValueType;
  city: FormTextFieldValueType;
  phone: FormTextFieldValueType;
  email: FormTextFieldValueType;
  privacyConfirmation: FormCheckboxValueType;
  installAddressDiffers: FormMultipleChoiceValueType;
  installAddressStreet: FormTextFieldValueType;
  installAddressStreetNumber: FormTextFieldValueType;
  installAddressZipcode: FormTextFieldValueType;
  installAddressCity: FormTextFieldValueType;
}

const config: StepDefinition = {
  component,
  fields: [
    buildFormTextField('companyName', {
      outputFormatter: (data) => ({
        formattedLabel:
          data?.formattedValue !== '–' ? data.formattedLabel : false,
        formattedValue:
          data?.formattedValue !== '–' ? data.formattedValue : false,
      }),
    }),
    buildFormTextField('firstname', {
      required: true,
    }),
    buildFormTextField('lastname', {
      required: true,
    }),
    buildFormTextField('street', {
      required: true,
    }),
    buildFormTextField('streetNumber', {
      required: true,
    }),
    buildFormTextField('zipcode', {
      required: true,
      validation: [ValidationType.postalCodeDE],
    }),
    buildFormTextField('city', {
      required: true,
    }),
    buildFormTextField('phone', {
      required: true,
      validation: [ValidationType.phoneNumber],
    }),
    buildFormTextField('email', {
      component: {
        type: 'email',
      },
      required: true,
      validation: [ValidationType.email],
    }),
    buildFormMultipleChoiceField('installAddressDiffers', {
      component: {
        options: [
          {
            value: 'yes',
          },
          {
            value: 'no',
          },
        ],
        columns: 2,
        singleAnswer: true,
      },
      required: true,
      outputFormatter: (data) => ({
        formattedLabel: i18n.t(
          'contactForm.formFields.installAddressDiffers.formattedLabel',
        ) as string,
        formattedValue: data.formattedValue,
      }),
    }),
    buildFormTextField('installAddressStreet', {
      required: false,
      outputFormatter: (data) => ({
        formattedLabel:
          data?.formattedValue !== '–' ? data.formattedLabel : false,
        formattedValue:
          data?.formattedValue !== '–' ? data.formattedValue : false,
      }),
    }),
    buildFormTextField('installAddressStreetNumber', {
      required: false,
      outputFormatter: (data) => ({
        formattedLabel:
          data?.formattedValue !== '–' ? data.formattedLabel : false,
        formattedValue:
          data?.formattedValue !== '–' ? data.formattedValue : false,
      }),
    }),
    buildFormTextField('installAddressZipcode', {
      required: false,
      validation: [ValidationType.postalCodeDE],
      outputFormatter: (data) => ({
        formattedLabel:
          data?.formattedValue !== '–' ? data.formattedLabel : false,
        formattedValue:
          data?.formattedValue !== '–' ? data.formattedValue : false,
      }),
    }),
    buildFormTextField('installAddressCity', {
      required: false,
      outputFormatter: (data) => ({
        formattedLabel:
          data?.formattedValue !== '–' ? data.formattedLabel : false,
        formattedValue:
          data?.formattedValue !== '–' ? data.formattedValue : false,
      }),
    }),
    buildFormCheckbox('privacyConfirmation', {
      component: {
        showLabel: true,
      },
      hideLabel: true,
      required: true,
      outputFormatter: () => ({
        formattedLabel: false,
        formattedValue: false,
      }),
    }),
  ],
  id: StepId.CONTACT_FORM,
  type: StepTypes.CONTACT_FORM,
};

export default config;
